/**
 * DailyProofPanel — Main Daily Rounds Console
 * 
 * The core daily training interface of HumanReplica.ai.
 * Users train their Replica every day in under 2 minutes through this panel.
 * 
 * State Machine:
 * - READY: Show TodaysCaseCard, ready to generate
 * - GENERATING: Loading state while drafts are being generated
 * - COMPARING: Show DraftComparison + DailyActionBox
 * - EDITING: Inline edit mode (handled within DailyActionBox)
 * - COMPLETED: Show InstantProofPanel with metrics
 * - LOCKED: Already trained today
 * 
 * Core Principles:
 * - Human is always final authority
 * - Replica learns only from explicit human supervision
 * - Training must be measurable, explainable, and auditable
 * - Specialization must be provably better than generic AI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';

// Components
import { GovernanceStrip } from '../components/training/GovernanceStrip';
import { TodaysCaseCard } from '../components/training/TodaysCaseCard';
import { DraftComparison } from '../components/training/DraftComparison';
import { DailyActionBox } from '../components/training/DailyActionBox';
import { InstantProofPanel } from '../components/training/InstantProofPanel';
import { TrainingHistory } from '../components/training/TrainingHistory';
import { SurpriseMetricsCard } from '../components/training/SurpriseMetricsCard';
import CognitiveDNAPanel from '../components/training/CognitiveDNAPanel';
import CoachingMotivation from '../components/training/CoachingMotivation';
import TrainingSession from '../components/training/TrainingSession';
import LevelIndicator from '../components/training/LevelIndicator';
import { useReplica } from '../contexts/ReplicaContext';
import { useAuth } from '../contexts/AuthContext';

// Types
import type {
    TrainingCase,
    CaseSource,
    ContextPack,
    DraftPair,
    PanelState,
    TrainingAction,
    FeedbackReason,
    TrainingEvent,
    CaseEvaluation
} from '../types/trainingTypes';

// Utils
import { generateEvaluation } from '../lib/scoringUtils';
import {
    generateDemoCase,
    generateDemoDrafts,
    getDemoScenario,
    hasTrainedToday,
    getTodaysEvent,
    saveTrainingEvent,
    addToExamSet,
    generateId
} from '../lib/trainingStorage';
import type { SurpriseMetrics } from '../lib/surprise';

interface DailyProofPanelProps {
    onBack?: () => void;
}

export const DailyProofPanel: React.FC<DailyProofPanelProps> = ({ onBack }) => {
    // ============================================
    // STATE
    // ============================================

    const { user } = useAuth();
    const { getUserReplicas } = useReplica();
    const userReplicas = user?.uid ? getUserReplicas(user.uid) : [];
    const primaryReplica = userReplicas[0];

    const [panelState, setPanelState] = useState<PanelState>('READY');
    const [trainingCase, setTrainingCase] = useState<TrainingCase | null>(null);
    const [drafts, setDrafts] = useState<DraftPair | null>(null);
    const [completedAction, setCompletedAction] = useState<TrainingAction | null>(null);
    const [evaluation, setEvaluation] = useState<CaseEvaluation | null>(null);
    const [isAddedToExamSet, setIsAddedToExamSet] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentSurprise, setCurrentSurprise] = useState<SurpriseMetrics | null>(null);
    const [showTraining, setShowTraining] = useState(false);

    // ============================================
    // INITIALIZATION
    // ============================================

    useEffect(() => {
        initializePanel();
    }, []);

    const initializePanel = () => {
        // Check if already trained today
        if (hasTrainedToday()) {
            const todaysEvent = getTodaysEvent();
            if (todaysEvent) {
                setCompletedAction(todaysEvent.action);
                setEvaluation(todaysEvent.evaluation);
                if (todaysEvent.baselineDraft && todaysEvent.replicaDraft) {
                    setDrafts({
                        baseline: todaysEvent.baselineDraft,
                        replica: todaysEvent.replicaDraft
                    });
                }
                setPanelState('LOCKED');
                return;
            }
        }

        // Generate today's case
        const newCase = generateDemoCase();
        setTrainingCase(newCase);
        setPanelState('READY');
    };

    // ============================================
    // CASE HANDLERS
    // ============================================

    const handleSourceChange = (source: CaseSource) => {
        if (!trainingCase) return;

        if (source === 'quick_case') {
            // Regenerate demo case
            const scenario = getDemoScenario();
            setTrainingCase({
                ...trainingCase,
                source,
                title: scenario.title,
                prompt: scenario.prompt
            });
        } else {
            // Clear for user input
            setTrainingCase({
                ...trainingCase,
                source,
                title: '',
                prompt: ''
            });
        }
    };

    const handleContextPackChange = (contextPack: ContextPack) => {
        if (!trainingCase) return;
        setTrainingCase({ ...trainingCase, contextPack });
    };

    const handleTitleChange = (title: string) => {
        if (!trainingCase) return;
        setTrainingCase({ ...trainingCase, title });
    };

    const handlePromptChange = (prompt: string) => {
        if (!trainingCase) return;
        setTrainingCase({ ...trainingCase, prompt });
    };

    // ============================================
    // DRAFT GENERATION
    // ============================================

    const handleGenerateDrafts = useCallback(async () => {
        if (!trainingCase) return;

        setPanelState('GENERATING');

        // Simulate generation delay (TODO: Replace with actual API call)
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Generate demo drafts
        const scenario = getDemoScenario();
        const generatedDrafts = generateDemoDrafts(scenario);

        setDrafts(generatedDrafts);
        setCurrentSurprise(scenario.surprise || null);
        setPanelState('COMPARING');
    }, [trainingCase]);

    // ============================================
    // TRAINING ACTIONS
    // ============================================

    const handleApprove = async () => {
        if (!trainingCase || !drafts) return;

        setIsSubmitting(true);

        // Simulate processing
        await new Promise(resolve => setTimeout(resolve, 500));

        const eval_ = generateEvaluation(
            drafts.baseline,
            drafts.replica,
            trainingCase.contextPack,
            []
        );

        const event: TrainingEvent = {
            id: generateId(),
            timestamp: new Date().toISOString(),
            caseId: trainingCase.id,
            caseTitle: trainingCase.title,
            action: 'approve',
            feedbackReasons: [],
            evaluation: eval_,
            baselineDraft: drafts.baseline,
            replicaDraft: drafts.replica
        };

        saveTrainingEvent(event);
        setCompletedAction('approve');
        setEvaluation(eval_);
        setPanelState('COMPLETED');
        setIsSubmitting(false);
    };

    const handleEditAndTeach = async (
        editedText: string,
        reasons: FeedbackReason[],
        note?: string
    ) => {
        if (!trainingCase || !drafts) return;

        setIsSubmitting(true);

        await new Promise(resolve => setTimeout(resolve, 500));

        const eval_ = generateEvaluation(
            drafts.baseline,
            drafts.replica,
            trainingCase.contextPack,
            reasons
        );

        const event: TrainingEvent = {
            id: generateId(),
            timestamp: new Date().toISOString(),
            caseId: trainingCase.id,
            caseTitle: trainingCase.title,
            action: 'edit_teach',
            feedbackReasons: reasons,
            feedbackNote: note,
            editedReplicaText: editedText,
            evaluation: eval_,
            baselineDraft: drafts.baseline,
            replicaDraft: drafts.replica
        };

        saveTrainingEvent(event);
        setCompletedAction('edit_teach');
        setEvaluation(eval_);
        setPanelState('COMPLETED');
        setIsSubmitting(false);
    };

    const handleRejectAndExplain = async (
        reasons: FeedbackReason[],
        note?: string
    ) => {
        if (!trainingCase || !drafts) return;

        setIsSubmitting(true);

        await new Promise(resolve => setTimeout(resolve, 500));

        // Lower scores for rejected outputs
        const eval_ = generateEvaluation(
            drafts.baseline,
            drafts.replica,
            trainingCase.contextPack,
            reasons
        );
        // Adjust scores for rejection
        eval_.representationQuality = Math.max(0, eval_.representationQuality - 20);

        const event: TrainingEvent = {
            id: generateId(),
            timestamp: new Date().toISOString(),
            caseId: trainingCase.id,
            caseTitle: trainingCase.title,
            action: 'reject_explain',
            feedbackReasons: reasons,
            feedbackNote: note,
            evaluation: eval_,
            baselineDraft: drafts.baseline,
            replicaDraft: drafts.replica
        };

        saveTrainingEvent(event);
        setCompletedAction('reject_explain');
        setEvaluation(eval_);
        setPanelState('COMPLETED');
        setIsSubmitting(false);
    };

    const handleAddToExamSet = () => {
        if (!trainingCase) return;
        addToExamSet(trainingCase.id);
        setIsAddedToExamSet(true);
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <div className="min-h-screen bg-hig-bg-primary">
            {/* Governance Strip - Always Visible */}
            <GovernanceStrip
                humanApprovalRequired={true}
                guardianActive={true}
                auditRecording={true}
                surpriseActive={currentSurprise !== null}
                onDataBoundariesClick={() => {
                    // TODO: Navigate to data boundaries settings
                    console.log('Open data boundaries');
                }}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="p-2 rounded-lg bg-hig-bg-secondary border border-hig-border hover:bg-hig-bg-tertiary transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 text-hig-text-secondary" />
                            </button>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-hig-text-primary">
                                Daily Rounds
                            </h1>
                            <p className="text-sm text-hig-text-tertiary">
                                Train your Replica in under 2 minutes
                            </p>
                        </div>
                    </div>

                    {/* Panel State Indicator */}
                    <div className="flex items-center gap-2">
                        <StateIndicator state={panelState} />
                    </div>
                </div>

                {/* Main Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Main Panel - 3 columns */}
                    <div className="lg:col-span-3 space-y-6">

                        {/* Cognitive Twin Panels — DNA: 60% / Coaching: 38% (Fix 2) */}
                        <div style={{ display: 'grid', gridTemplateColumns: '60% 38%', gap: '2%' }}>
                            <CognitiveDNAPanel
                                replicaId={primaryReplica?.id || 'default'}
                                replicaName={primaryReplica?.name}
                                compact
                            />
                            <CoachingMotivation compact />
                        </div>

                        {/* Interactive Training Session */}
                        {showTraining ? (
                            <TrainingSession
                                replicaId={primaryReplica?.id || 'default'}
                                onComplete={() => {
                                    // Refresh happens on next load
                                }}
                                onClose={() => setShowTraining(false)}
                            />
                        ) : (
                            <button
                                onClick={() => setShowTraining(true)}
                                className="w-full"
                                style={{
                                    padding: '16px 24px',
                                    borderRadius: 14,
                                    border: '1px dashed rgba(167,139,250,0.3)',
                                    background: 'rgba(167,139,250,0.05)',
                                    color: '#a78bfa',
                                    fontSize: 15,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 10,
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                🧠 Start Training Session — Shadow → Correct → Debrief
                            </button>
                        )}

                        {/* READY State */}
                        {panelState === 'READY' && trainingCase && (
                            <TodaysCaseCard
                                trainingCase={trainingCase}
                                onSourceChange={handleSourceChange}
                                onContextPackChange={handleContextPackChange}
                                onTitleChange={handleTitleChange}
                                onPromptChange={handlePromptChange}
                                onGenerateDrafts={handleGenerateDrafts}
                                isGenerating={false}
                            />
                        )}

                        {/* GENERATING State */}
                        {panelState === 'GENERATING' && (
                            <div className="card-elevated p-12 text-center">
                                <div className="w-12 h-12 border-3 border-hig-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-hig-text-primary mb-2">
                                    Generating Drafts
                                </h3>
                                <p className="text-sm text-hig-text-tertiary">
                                    Preparing baseline and specialized outputs...
                                </p>
                            </div>
                        )}

                        {/* COMPARING State */}
                        {panelState === 'COMPARING' && drafts && (
                            <>
                                <DraftComparison
                                    baseline={drafts.baseline}
                                    replica={drafts.replica}
                                />
                                {currentSurprise && (
                                    <SurpriseMetricsCard surprise={currentSurprise} />
                                )}
                                <DailyActionBox
                                    replicaText={drafts.replica.text}
                                    onApprove={handleApprove}
                                    onEditAndTeach={handleEditAndTeach}
                                    onRejectAndExplain={handleRejectAndExplain}
                                    isSubmitting={isSubmitting}
                                />
                            </>
                        )}

                        {/* COMPLETED State */}
                        {panelState === 'COMPLETED' && completedAction && evaluation && drafts && (
                            <InstantProofPanel
                                action={completedAction}
                                evaluation={evaluation}
                                baseline={drafts.baseline}
                                replica={drafts.replica}
                                onAddToExamSet={handleAddToExamSet}
                                isAddedToExamSet={isAddedToExamSet}
                            />
                        )}

                        {/* LOCKED State */}
                        {panelState === 'LOCKED' && (
                            <div className="card-elevated p-12 text-center">
                                <div className="w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-4 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-hig-text-primary mb-2">
                                    Today's Training Complete
                                </h2>
                                <p className="text-hig-text-secondary mb-6">
                                    You've already trained your Replica today. Come back tomorrow for your next session.
                                </p>

                                {/* Show today's results summary */}
                                {evaluation && (
                                    <div className="inline-block p-4 bg-hig-bg-secondary rounded-xl border border-hig-border">
                                        <div className="text-xs text-hig-text-tertiary uppercase tracking-wide mb-1">
                                            Today's Score
                                        </div>
                                        <div className="text-3xl font-bold text-green-400">
                                            {evaluation.representationQuality}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar - 1 column */}
                    <div className="lg:col-span-1 space-y-4">
                        <LevelIndicator
                            replicaId={primaryReplica?.id || 'default'}
                        />
                        <TrainingHistory />
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================
// STATE INDICATOR SUB-COMPONENT
// ============================================

interface StateIndicatorProps {
    state: PanelState;
}

const StateIndicator: React.FC<StateIndicatorProps> = ({ state }) => {
    const stateConfig: Record<PanelState, { label: string; color: string }> = {
        READY: { label: 'Ready', color: 'bg-hig-accent' },
        GENERATING: { label: 'Generating', color: 'bg-yellow-500' },
        COMPARING: { label: 'Awaiting Action', color: 'bg-purple-500' },
        EDITING: { label: 'Editing', color: 'bg-yellow-500' },
        COMPLETED: { label: 'Completed', color: 'bg-green-500' },
        LOCKED: { label: 'Done Today', color: 'bg-gray-500' }
    };

    const config = stateConfig[state];

    return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-hig-bg-secondary rounded-full border border-hig-border">
            <span className={`w-2 h-2 rounded-full ${config.color}`} />
            <span className="text-xs font-medium text-hig-text-secondary uppercase tracking-wide">
                {config.label}
            </span>
        </div>
    );
};

export default DailyProofPanel;
